import { z } from "zod";
import { DecisionKeySchema, DeviceClassSchema, PhaseKeySchema, SubmitReasonSchema, SurveyAnswersSchema, SurveyAudienceSchema, } from "./domain.js";
export const EventTypeSchema = z.enum([
    "join",
    "resume",
    "consent",
    "start",
    "phase_enter",
    "phase_exit",
    "visibility",
    "focus",
    "idle",
    "connectivity",
    "document_open",
    "document_dwell",
    "block_visibility",
    "scroll_depth",
    "copy",
    "find",
    "slider_change",
    "confidence_change",
    "draft_save",
    "draft_paste",
    "decision_submit",
    "decision_confirm",
    "survey_draft",
    "survey_submit",
    "heartbeat",
    "client_error",
]);
export const ClientEventSchema = z.object({
    id: z.string().uuid(),
    seq: z.number().int().positive(),
    type: EventTypeSchema,
    phase_key: PhaseKeySchema.nullable().optional(),
    client_time: z.string().datetime(),
    phase_elapsed_ms: z.number().int().nonnegative().nullable().optional(),
    payload: z.record(z.unknown()).default({}),
});
export const DecisionDraftPayloadSchema = z.object({
    decision_key: DecisionKeySchema,
    share_a: z.number().int().min(0).max(100).nullable(),
    slider_touched: z.boolean(),
    confidence: z.number().int().min(1).max(7).nullable(),
    confidence_touched: z.boolean(),
    justification: z.string().max(1000),
    revision: z.number().int().positive(),
});
export const DecisionSubmitPayloadSchema = z.object({
    attempt_id: z.string().uuid(),
    decision_key: DecisionKeySchema,
    share_a: z.number().int().min(0).max(100).nullable(),
    slider_touched: z.boolean(),
    confidence: z.number().int().min(1).max(7).nullable(),
    confidence_touched: z.boolean(),
    justification: z.string().max(1000),
    reason: SubmitReasonSchema,
    client_time: z.string().datetime(),
    draft_revision: z.number().int().nonnegative().optional(),
});
export const SurveyDraftPayloadSchema = z.object({
    answers: SurveyAnswersSchema,
    revision: z.number().int().positive(),
});
export const SurveySubmitPayloadSchema = z.object({
    attempt_id: z.string().uuid(),
    answers: SurveyAnswersSchema,
    reason: SubmitReasonSchema,
    client_time: z.string().datetime(),
    draft_revision: z.number().int().nonnegative().optional(),
});
export const JoinByLinkSchema = z.object({
    link_token: z.string().min(16),
    device_class: DeviceClassSchema.default("unknown"),
    screen_w: z.number().int().positive().optional(),
    screen_h: z.number().int().positive().optional(),
    user_agent: z.string().max(500).optional(),
    locale: z.string().max(32).optional(),
    app_build: z.string().max(64).optional(),
});
export const JoinByCodeSchema = z.object({
    short_code: z.string().min(4).max(12),
    device_class: DeviceClassSchema.default("unknown"),
    screen_w: z.number().int().positive().optional(),
    screen_h: z.number().int().positive().optional(),
    user_agent: z.string().max(500).optional(),
    locale: z.string().max(32).optional(),
    app_build: z.string().max(64).optional(),
});
export const ConsentSchema = z.object({
    consent_version: z.string().min(1),
});
export const StartSchema = z.object({
    client_time: z.string().datetime(),
    tab_visible: z.literal(true),
});
export const AdvancePhaseSchema = z.object({
    client_time: z.string().datetime(),
    tab_visible: z.literal(true),
    /** Phase the tab thinks it is on. A second tab must not advance a later phase. */
    phase: z.string().min(1).optional(),
});
export const BatchEventsSchema = z.object({
    events: z.array(ClientEventSchema).max(100),
});
export const AckSchema = z.object({
    ack_ids: z.array(z.string().uuid()),
});
export const DocumentViewSchema = z.object({
    id: z.string(),
    title: z.string(),
    html: z.string(),
    order: z.number().int(),
});
export const ParticipantStateSchema = z.object({
    public_code: z.string(),
    status: z.string(),
    phase: PhaseKeySchema,
    phase_entered_at: z.string().datetime().nullable(),
    phase_deadline_at: z.string().datetime().nullable(),
    phase_earliest_continue_at: z.string().datetime().nullable(),
    server_now: z.string().datetime(),
    remaining_ms: z.number().int().nullable(),
    documents: z.array(DocumentViewSchema).optional(),
    frame_html: z.string().optional(),
    decision: z
        .object({
        key: DecisionKeySchema,
        prompt_html: z.string(),
        option_a: z.string(),
        option_b: z.string(),
        budget_total: z.number(),
        currency: z.string(),
        show_previous: z.boolean(),
        previous: z
            .object({
            share_a: z.number().nullable(),
            justification: z.string(),
            confidence: z.number().nullable(),
        })
            .nullable(),
        draft: z
            .object({
            share_a: z.number().nullable(),
            slider_touched: z.boolean(),
            confidence: z.number().nullable(),
            confidence_touched: z.boolean(),
            justification: z.string(),
            revision: z.number(),
        })
            .nullable(),
        min_chars: z.number(),
        max_chars: z.number(),
    })
        .optional(),
    survey: z
        .object({
        draft: SurveyAnswersSchema.nullable(),
        revision: z.number(),
        submitted: z.boolean(),
        audience: SurveyAudienceSchema.default("board"),
    })
        .optional(),
    visible_docs: z.array(DocumentViewSchema).optional(),
    end: z
        .object({
        html: z.string(),
        message: z.string().optional(),
        contact_email: z.string().optional(),
        public_code: z.string().optional(),
    })
        .optional(),
    session_ended: z.boolean().optional(),
    config_placeholders: z
        .object({
        base_minutes: z.number(),
        latest_start: z.string(),
    })
        .optional(),
    is_test: z.boolean().optional(),
    contact_email: z.string().optional(),
});
export const CreateRunSchema = z.object({
    label: z.string().min(1).max(200),
    package_id: z.string().min(1),
    treatment_variant: z.string().min(1),
    placebo_variant: z.string().min(1),
    ai_brief_variant: z.string().min(1),
    is_test: z.boolean().default(false),
    cap: z.number().int().positive().default(1000),
    block_size: z.number().int().positive().default(4),
    abandon_after_ms: z.number().int().positive().default(10 * 60_000),
    earliest_start: z.string().min(1),
    latest_start: z.string().min(1),
    link_opens_minutes_before: z.number().int().nonnegative().default(60),
    phases: z
        .array(z.object({
        key: z.string(),
        min_ms: z.number().int().nonnegative().optional(),
        max_ms: z.number().int().positive(),
    }))
        .optional(),
    sped_up: z.boolean().optional(),
    survey_enabled: z.boolean().default(true),
    survey_audience: SurveyAudienceSchema.optional(),
    survey_max_ms: z.number().int().positive().optional(),
});
export const AdminLoginSchema = z.object({
    // Username login (not email). min(1) so short local passwords remain valid.
    username: z.string().min(1).max(100),
    password: z.string().min(1).max(200),
    totp_code: z.string().length(6).optional(),
});
export const MonitorCountsSchema = z.object({
    run_id: z.string(),
    label: z.string(),
    status: z.string(),
    frozen: z.boolean(),
    total_participants: z.number(),
    by_status: z.record(z.number()),
    by_phase: z.record(z.number()),
    by_arm: z.record(z.number()),
    started: z.number(),
    completed: z.number(),
    abandoned: z.number(),
});
//# sourceMappingURL=api.js.map